import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, Col, Flex } from 'antd';
import { useMemo, useState } from 'react';

import { Link } from '@suntex.core/nav';
import {
  $RankingLevel,
  $SpecialGradeLevel,
  LoginType,
  RankingLevel,
  SpecialGradeLevel,
} from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput,
  Table,
  TableModel,
  Toolbar,
  YearSelect,
} from '@suntex.fim/ui';

import { RankingsSlug } from '../const';
import { RankingResponseDataType, defineTables } from '../tables';
import { buildExportMenu } from '../utils/assessmentReportUtils';
import {
  getDefaultGrade,
  getGradeOptions,
  shouldDisplayLevelDropdown,
  shouldDisplayState,
} from '../utils/rankingUtils';

export interface RankingsTableProps {
  rankingSlug: RankingsSlug;
  // Optional props for customization
  searchParams: {
    grade?: number | null;
    year?: string | null;
    rankingLevel?: string | null;
  };
  user?: { loginType: LoginType; stateName?: string };
}

/**
 * CommonRanking component - A reusable ranking table component
 */
export function RankingsTable({
  rankingSlug,
  searchParams: queryString,
  user,
}: RankingsTableProps) {
  const { api, calendar, localization } = useFramework();
  const tableDef = useMemo(
    () => defineTables(api)[rankingSlug],
    [api, rankingSlug],
  );
  const [tableModel, setTableModel] =
    useState<TableModel<RankingResponseDataType> | null>(null);
  const gradeOptions = getGradeOptions(rankingSlug);
  const headerTitle = shouldDisplayState(rankingSlug)
    ? `${tableDef.title} - ${user?.stateName}`
    : tableDef.title;
  const levelOptions = $RankingLevel.listOptions();
  const shouldDisplayFilters =
    rankingSlug !== RankingsSlug.PlayerIndividualNation;
  const defaultgrade = getDefaultGrade(rankingSlug);
  const previousYear = calendar.getPreviousSeason();
  const currentYear = calendar.getCurrentSeason();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(() => {
    return {
      grade: $SpecialGradeLevel.from(queryString.grade ?? defaultgrade),
      year: queryString.year ?? currentYear,
      level: $RankingLevel.from(
        queryString.rankingLevel ?? RankingLevel.Diamond,
      ),
    };
  });
  const { isLoading, data: rankingData } = useQuery({
    queryKey: [
      'rankings',
      rankingSlug,
      filters.grade,
      filters.year,
      filters.level,
    ],
    queryFn: async () => {
      return await tableDef.loader(filters);
    },
  });

  return (
    <div>
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        <LoaderOverlay isLoading={isLoading} />
        <Breadcrumb
          className={'print-hidden'}
          items={[
            {
              title: <Link to={'..'}>Rankings</Link>,
            },
            {
              title: <b>{headerTitle}</b>,
            },
          ]}
        />
        <Toolbar
          left={
            <>
              {shouldDisplayFilters && (
                <>
                  <Col>
                    <div className="label">{localization.text('Grade')}:</div>
                    <LevelSelect<SpecialGradeLevel>
                      value={filters.grade}
                      onChange={(grade) => {
                        setFilters((prev) => ({ ...prev, grade }));
                        setSearchQuery('');
                      }}
                      options={gradeOptions}
                    />
                  </Col>
                  <Col>
                    <div className="label">School Year:</div>
                    <YearSelect
                      value={filters.year}
                      onChange={(year) => {
                        setFilters((prev) => ({ ...prev, year }));
                        setSearchQuery('');
                      }}
                      options={[previousYear, currentYear]}
                    />
                  </Col>
                </>
              )}
              {shouldDisplayLevelDropdown(rankingSlug) && (
                <Col>
                  <div className="label">Level:</div>
                  <LevelSelect<RankingLevel>
                    value={filters.level}
                    onChange={(level) => {
                      setFilters((prev) => ({ ...prev, level }));
                      setSearchQuery('');
                    }}
                    options={levelOptions}
                  />
                </Col>
              )}
            </>
          }
          right={
            <>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
              {tableModel && (
                <ExportMenu
                  items={buildExportMenu({
                    tableModel,
                    fileName: rankingSlug,
                    localize: localization.text,
                  })}
                />
              )}
            </>
          }
        />
        <Table<RankingResponseDataType>
          {...{
            data: rankingData ?? [],
            columns: tableDef.columns,
            isLoading,
            globalFilter: searchQuery,
            initialSorting: [{ id: 'topRanking', desc: false }],
            hideFirstTwoColumns: false,
            hideFirstColumns: false,
            setTableModel,
          }}
        />
      </Flex>
    </div>
  );
}
